import React from 'react';
import { Chip } from '@mui/material';
import { CheckCircle as ApprovedIcon, Cancel as RejectedIcon, Schedule as PendingIcon } from '@mui/icons-material';
import { WriteoffApprovalView } from '../../../../../../services/WriteoffService/types/writeoffTypes';

/** Status configuration for approval chips */
export const APPROVAL_STATUS_CONFIG = {
  'Pending': {
    color: 'warning' as const,
    icon: <PendingIcon sx={{ fontSize: 16 }} />,
    bgColor: '#fff3cd',
    textColor: '#856404'
  },
  'Approved': {
    color: 'success' as const,
    icon: <ApprovedIcon sx={{ fontSize: 16 }} />,
    bgColor: '#d1edff',
    textColor: '#0c5460'
  },
  'Rejected': {
    color: 'error' as const,
    icon: <RejectedIcon sx={{ fontSize: 16 }} />,
    bgColor: '#f8d7da',
    textColor: '#721c24'
  }
} as const;

export type ApprovalStatus = keyof typeof APPROVAL_STATUS_CONFIG;

/** Get the approval status from an approval record */
export const getApprovalStatus = (approval: WriteoffApprovalView): ApprovalStatus => {
  if (approval.isRejected) return 'Rejected';
  if (approval.isApproved) return 'Approved';
  return 'Pending';
};

interface ApprovalStatusChipProps {
  approval: WriteoffApprovalView;
}

/**
 * Displays approval status with color-coded chip and icon.
 */
export const ApprovalStatusChip: React.FC<ApprovalStatusChipProps> = ({ approval }) => {
  const status = getApprovalStatus(approval);
  const config = APPROVAL_STATUS_CONFIG[status];

  return (
    <Chip
      icon={config.icon}
      label={status}
      size="small"
      sx={{
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontWeight: 500,
        fontSize: '0.75rem',
        '& .MuiChip-icon': {
          color: config.textColor
        }
      }}
    />
  );
};

/** Syncfusion grid template wrapper */
export const approvalStatusTemplate = (props: WriteoffApprovalView) => (
  <ApprovalStatusChip approval={props} />
);

export default ApprovalStatusChip;
